import { useState } from "react";
import { Check, Mail } from "lucide-react";

import { WhatsappIcon } from "@/components/common/whatsapp-brand";
import { WhatsappOptinDialog } from "@/components/settings/WhatsappOptinDialog";
import { FREE_DAILY_ALERTS_UNTIL } from "@/constants";
import {
  isWhatsappActive,
  useWhatsappStatus,
} from "@/hooks/queries/useWhatsappStatus";
import { ActionBar, ActionButton, Eyebrow, ScreenTitle, type Tone } from "./OnboardingShell";

function ActivePill() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-[13px] font-bold text-emerald-700">
      <Check className="size-3.5" strokeWidth={3} />
      Activo
    </span>
  );
}

/**
 * Último paso de cada flujo: el correo ya va activo y WhatsApp se vincula igual
 * en todo plan (vincularlo no nos cuesta). Sale en el resumen de las 9:00, por
 * eso dice "cada mañana".
 */
export function OnboardingAlertsStep({
  tone,
  title,
  isFree,
  onFinish,
  finishing,
}: {
  tone: Tone;
  title: string;
  isFree: boolean;
  onFinish: () => void;
  finishing: boolean;
}) {
  const [optinOpen, setOptinOpen] = useState(false);
  const { data: whatsapp } = useWhatsappStatus({ poll: optinOpen });
  const whatsappActive = isWhatsappActive(whatsapp);

  return (
    <div className="mx-auto w-full max-w-[720px]">
      <Eyebrow tone={tone}>Avisos</Eyebrow>
      <ScreenTitle>{title}</ScreenTitle>

      <div className="mt-7 space-y-3">
        <div className="flex items-center gap-4 rounded-[20px] border border-slate-200 bg-white p-4 sm:p-5">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-red-50 text-red-500">
            <Mail className="size-5" strokeWidth={2.2} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[16px] font-bold text-slate-900">Correo</p>
            <p className="text-[13.5px] text-slate-500">
              {isFree
                ? `Resumen diario a las 9:00 hasta el ${FREE_DAILY_ALERTS_UNTIL}; luego semanal.`
                : "Resumen diario a las 9:00."}
            </p>
          </div>
          <ActivePill />
        </div>

        <div className="rounded-[20px] border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center gap-4">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-green-50 text-green-600">
              <WhatsappIcon className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[16px] font-bold text-slate-900">WhatsApp, cada mañana</p>
              <p className="text-[13.5px] text-slate-500">
                {whatsappActive && whatsapp?.number
                  ? `+${whatsapp.number}`
                  : "El mismo resumen en tu celular, a primera hora."}
              </p>
            </div>
            {whatsappActive ? (
              <ActivePill />
            ) : (
              whatsapp?.optinUrl && (
                <button
                  type="button"
                  onClick={() => setOptinOpen(true)}
                  className="inline-flex shrink-0 cursor-pointer items-center rounded-[10px] bg-[#25D366] px-4 py-2 text-[13.5px] font-bold text-white transition-colors hover:bg-[#1DA851]"
                >
                  Activar
                </button>
              )
            )}
          </div>
          {!whatsappActive && whatsapp?.optinUrl && (
            <p className="mt-3 border-t border-slate-100 pt-3 text-[12.5px] leading-[1.55] text-slate-500">
              Te abrimos el chat con el mensaje listo: solo tienes que enviarlo. Queda verificado
              el número desde el que nos escribas.
            </p>
          )}
        </div>
      </div>

      <ActionBar>
        <ActionButton tone={tone} onClick={onFinish} loading={finishing}>
          Ver mis oportunidades
        </ActionButton>
      </ActionBar>

      {whatsapp?.optinUrl && (
        <WhatsappOptinDialog
          open={optinOpen}
          onOpenChange={setOptinOpen}
          optinUrl={whatsapp.optinUrl}
          verified={whatsappActive}
        />
      )}
    </div>
  );
}
